import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadCompanyProfile } from '../utils/companyProfileStorage';

const QuotePreviewDialog = ({ open, onClose, quote, company }) => {
  const profile = loadCompanyProfile() || {};
  let storedUser = null;
  try {
    const rawUser = localStorage.getItem('user');
    storedUser = rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    storedUser = null;
  }

  const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
  const backendOrigin = apiBaseUrl.replace(/\/(api)\/?$/, '');

  const resolveAssetUrl = (value) => {
    if (!value) return '';
    if (typeof value !== 'string') return '';
    if (/^data:/i.test(value)) return value;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/uploads/')) return `${backendOrigin}${value}`;
    return value;
  };

  const issuerName = profile.name || storedUser?.companyName || storedUser?.company?.name || 'Mon entreprise';
  const issuerLogo = resolveAssetUrl(profile.logo || storedUser?.logo || storedUser?.company?.logo || '');
  const issuerAddressLine = [profile.address, profile.postalCode, profile.city].filter(Boolean).join(' ');
  const issuerSiret = profile.siret;
  const issuerPhone = profile.phone;
  const issuerEmail = profile.email || storedUser?.email;
  const issuerWebsite = profile.website;

  const issuerPrimary = profile.primaryColor || '#977F6B';
  const issuerSecondary = profile.secondaryColor || '#1F2937';
  const documentTheme = (profile.documentTheme || 'modern').toString().toLowerCase();

  const hexToRgb = (hexColor) => {
    const normalized = (hexColor || '').toString().trim().replace('#', '');
    if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return null;
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  };

  const rgbaFromHex = (hexColor, alpha) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return `rgba(0,0,0,${alpha})`;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  };

  const themeTokens = (() => {
    switch (documentTheme) {
      case 'classic':
        return {
          tableHeadBg: issuerPrimary,
          tableHeadText: '#ffffff',
          infoBg: '#f7f3ef',
          stripeA: '#fbfaf8',
          stripeB: '#f3ede7',
          totalLabelColor: issuerPrimary,
          radius: 10,
        };
      case 'minimal':
        return {
          tableHeadBg: '#111827',
          tableHeadText: '#ffffff',
          infoBg: '#f6f6f6',
          stripeA: '#ffffff',
          stripeB: '#f7f7f7',
          totalLabelColor: '#111827',
          radius: 8,
        };
      case 'modern':
      default:
        return {
          tableHeadBg: issuerSecondary,
          tableHeadText: '#ffffff',
          // Semi-transparent surfaces so the watermark stays visible while keeping readability.
          infoBg: rgbaFromHex('#ffffff', 0.65),
          stripeA: rgbaFromHex(issuerPrimary, 0.06),
          stripeB: rgbaFromHex(issuerPrimary, 0.03),
          totalLabelColor: issuerSecondary,
          radius: 12,
          pageBg: `linear-gradient(135deg, ${rgbaFromHex(issuerPrimary, 0.22)} 0%, ${rgbaFromHex(issuerSecondary, 0.12)} 45%, ${rgbaFromHex(issuerPrimary, 0.12)} 100%)`,
        };
    }
  })();

  const loadImageAsDataUrl = async (url) => {
    if (!url) return '';
    if (/^data:/i.test(url)) return url;

    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          resolve('');
        }
      };
      img.onerror = () => resolve('');
      img.src = url;
    });
  };

  const paginateCanvas = ({
    canvas,
    pageWidthMm,
    pageHeightMm,
    marginTopMm,
    marginBottomMm,
    marginLeftMm,
    marginRightMm,
    targetWidthMm,
    avoidRangesPx = [],
  }) => {
    const maxWidthMm = pageWidthMm - marginLeftMm - marginRightMm;
    const maxHeightMm = pageHeightMm - marginTopMm - marginBottomMm;

    const imgWidthMm = targetWidthMm && targetWidthMm <= maxWidthMm ? targetWidthMm : maxWidthMm;
    const pxPerMm = canvas.width / imgWidthMm;
    const pageSliceHeightPx = Math.floor(maxHeightMm * pxPerMm);
    const minSliceHeightPx = Math.max(120, Math.floor(pageSliceHeightPx * 0.25));
    const slicePaddingPx = Math.max(8, Math.floor(4 * pxPerMm));

    const findSafeBreakY = (startY, idealEndY) => {
      const endY = Math.min(idealEndY, canvas.height);

      // If the break falls inside an avoid-range, move the break up to just before that range.
      const intersecting = avoidRangesPx.find((range) => {
        const top = Math.max(0, range.topPx);
        const bottom = Math.min(canvas.height, range.bottomPx);
        return endY > top + slicePaddingPx && endY < bottom - slicePaddingPx;
      });

      if (!intersecting) return endY;

      const proposed = Math.max(startY + minSliceHeightPx, intersecting.topPx - slicePaddingPx);
      // If moving up would make the slice too small, keep the original break.
      if (proposed <= startY + minSliceHeightPx) return endY;

      return Math.min(proposed, endY);
    };

    const slices = [];
    let y = 0;
    while (y < canvas.height) {
      const idealEnd = y + pageSliceHeightPx;
      const safeEnd = findSafeBreakY(y, idealEnd);
      const sliceHeight = Math.min(Math.max(safeEnd - y, minSliceHeightPx), canvas.height - y);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      slices.push({
        canvas: sliceCanvas,
        widthMm: imgWidthMm,
        heightMm: (sliceHeight * imgWidthMm) / canvas.width,
      });
      y += sliceHeight;
    }

    return { slices, imgWidthMm, maxHeightMm, pxPerMm };
  };

  const drawPageBackground = (ctx, widthPx, heightPx) => {
    if (documentTheme === 'modern') {
      // Match the on-screen gradient direction (135deg ~ TL -> BR)
      const primaryRgb = hexToRgb(issuerPrimary) || { r: 184, g: 115, b: 51 };
      const secondaryRgb = hexToRgb(issuerSecondary) || { r: 31, g: 41, b: 55 };
      const grad = ctx.createLinearGradient(0, 0, widthPx, heightPx);
      grad.addColorStop(0, `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.22)`);
      grad.addColorStop(0.45, `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.12)`);
      grad.addColorStop(1, `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.12)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, widthPx, heightPx);
      return;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, widthPx, heightPx);
  };

  const composeA4Pages = async ({ element, pageWidthMm, pageHeightMm, margins }) => {
    const { topMm, bottomMm, leftMm, rightMm } = margins;

    // Identify DOM regions we do NOT want to cut across pages (totals/footer).
    const avoidDomElements = Array.from(element.querySelectorAll('.page-avoid-break'));
    const containerRect = element.getBoundingClientRect();
    const avoidRangesDomPx = avoidDomElements
      .map((node) => {
        const r = node.getBoundingClientRect();
        const topPx = (r.top - containerRect.top) + (element.scrollTop || 0);
        const bottomPx = topPx + r.height;
        return { topPx, bottomPx };
      })
      .filter((r) => Number.isFinite(r.topPx) && Number.isFinite(r.bottomPx) && r.bottomPx > r.topPx);

    const contentCanvas = await renderNodeToCanvas(element, {
      transparentBackground: true,
      disableWatermark: true,
    });

    const issuerLogoDataUrl = await loadImageAsDataUrl(issuerLogo);

    const domToCanvasScale = element.scrollWidth ? (contentCanvas.width / element.scrollWidth) : 1;
    const avoidRangesPx = avoidRangesDomPx.map((r) => ({
      topPx: Math.max(0, Math.floor(r.topPx * domToCanvasScale)),
      bottomPx: Math.min(contentCanvas.height, Math.ceil(r.bottomPx * domToCanvasScale)),
    }));

    const { slices, pxPerMm } = paginateCanvas({
      canvas: contentCanvas,
      pageWidthMm,
      pageHeightMm,
      marginTopMm: topMm,
      marginBottomMm: bottomMm,
      marginLeftMm: leftMm,
      marginRightMm: rightMm,
      targetWidthMm: pageWidthMm - leftMm - rightMm,
      avoidRangesPx,
    });

    const watermarkOpacity = documentTheme === 'modern' ? 0.12 : 0.06;

    const composedPages = await Promise.all(
      slices.map(async (slice) => {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = Math.round(pageWidthMm * pxPerMm);
        pageCanvas.height = Math.round(pageHeightMm * pxPerMm);

        const ctx = pageCanvas.getContext('2d');
        drawPageBackground(ctx, pageCanvas.width, pageCanvas.height);

        if (issuerLogoDataUrl) {
          const wmImg = new Image();
          wmImg.crossOrigin = 'anonymous';
          await new Promise((resolve) => {
            wmImg.onload = resolve;
            wmImg.onerror = resolve;
            wmImg.src = issuerLogoDataUrl;
          });
          const wmSize = Math.min(pageCanvas.width * 0.65, pageCanvas.height * 0.65);
          const wx = (pageCanvas.width - wmSize) / 2;
          const wy = (pageCanvas.height - wmSize) / 2;
          ctx.globalAlpha = watermarkOpacity;
          ctx.drawImage(wmImg, wx, wy, wmSize, wmSize);
          ctx.globalAlpha = 1;
        }

        const leftPx = Math.round(leftMm * pxPerMm);
        const rightPx = Math.round(rightMm * pxPerMm);
        const topPx = Math.round(topMm * pxPerMm);
        const printableWidthPx = pageCanvas.width - leftPx - rightPx;
        const scaledHeight = (slice.canvas.height * printableWidthPx) / slice.canvas.width;

        ctx.drawImage(
          slice.canvas,
          0,
          0,
          slice.canvas.width,
          slice.canvas.height,
          leftPx,
          topPx,
          printableWidthPx,
          scaledHeight
        );

        return pageCanvas;
      })
    );

    return composedPages;
  };

  const renderNodeToCanvas = async (
    node,
    { transparentBackground = false, disableWatermark = false } = {}
  ) => {
    const clone = node.cloneNode(true);
    clone.style.width = `${node.scrollWidth}px`;
    clone.style.height = 'auto';
    clone.style.overflow = 'visible';
    clone.style.boxSizing = 'border-box';

    if (transparentBackground) {
      clone.style.background = 'transparent';
      clone.style.backgroundColor = 'transparent';
    }

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.width = `${node.scrollWidth}px`;
    wrapper.style.background = transparentBackground ? 'transparent' : '#ffffff';
    wrapper.style.zIndex = '-1';

    if (disableWatermark || transparentBackground) {
      const style = document.createElement('style');
      style.type = 'text/css';
      style.appendChild(
        document.createTextNode(`
          #quote-preview::before { opacity: 0 !important; }
          ${transparentBackground ? '#quote-preview { background: transparent !important; background-color: transparent !important; }' : ''}
          /* Prevent flex + mt:auto footer from creating huge gaps on multi-page pagination */
          #quote-preview { display: block !important; }
          .quote-footer { margin-top: 32px !important; padding-top: 24px !important; }
        `)
      );
      wrapper.appendChild(style);
    }

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      return await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: transparentBackground ? null : '#ffffff',
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      });
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  const generatePDF = async () => {
    const element = document.getElementById('quote-preview');
    if (!element) return;

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const composedPages = await composeA4Pages({
        element,
        pageWidthMm: pageWidth,
        pageHeightMm: pageHeight,
        margins: { topMm: 8, bottomMm: 16, leftMm: 12, rightMm: 12 },
      });

      composedPages.forEach((pageCanvas, index) => {
        if (index > 0) pdf.addPage();
        const dataUrl = pageCanvas.toDataURL('image/png');
        pdf.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
      });

      pdf.save(`${quote.number}.pdf`);
    } catch (error) {
      console.error('Erreur lors de la génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  const handlePrint = async () => {
    const element = document.getElementById('quote-preview');
    if (!element) return;

    try {
      const composedPageCanvases = await composeA4Pages({
        element,
        pageWidthMm: 210,
        pageHeightMm: 297,
        margins: { topMm: 8, bottomMm: 16, leftMm: 12, rightMm: 12 },
      });

      const composedPages = composedPageCanvases.map((c) => c.toDataURL('image/png'));
      
      // Créer une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Devis ${quote.number}</title>
            <style>
              body { 
                margin: 0;
                padding: 0;
              }
              img { 
                width: 210mm;
                height: 297mm;
                display: block;
                page-break-after: always;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            ${composedPages.map((src) => `<img src="${src}" />`).join('')}
            <script>
              window.onload = () => setTimeout(() => { window.print(); window.close(); }, 200);
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de la préparation de l\'impression:', error);
      alert('Erreur lors de la préparation de l\'impression');
    }
  };

  if (!quote) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="between">
          <Typography variant="h6">
            Prévisualisation - Devis {quote.number}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box id="quote-preview" sx={{ 
          pt: 4,
          pb: 4,
          px: 3,
          background: documentTheme === 'modern' ? themeTokens.pageBg : 'white',
          backgroundColor: 'white',
          // A4 @ 96dpi ~= 1123px. This prevents a “white tail” when content is short.
          minHeight: documentTheme === 'modern' ? '1123px' : '800px',
          // A4 @ 96dpi ~= 794px wide.
          width: documentTheme === 'modern' ? { xs: '100%', md: '794px' } : '100%',
          mx: documentTheme === 'modern' ? 'auto' : 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: themeTokens.radius,
          ...(issuerLogo
            ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '58%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-8deg)',
                  width: '680px',
                  height: '680px',
                  backgroundImage: `url('${issuerLogo}')`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  opacity: documentTheme === 'modern' ? 0.32 : 0.05,
                  zIndex: 0,
                  pointerEvents: 'none'
                },
              }
            : {}),
          '& > *': {
            position: 'relative',
            zIndex: 1
          }
        }}>
          {/* En-tête entreprise */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={2}>
              {issuerLogo ? (
                <img
                  src={issuerLogo}
                  alt={`${issuerName} Logo`}
                  style={{ width: 112, height: 112, objectFit: 'contain' }}
                />
              ) : null}
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="h4" sx={{ color: '#1F2937', fontWeight: 'bold', mb: 1 }}>
                  {issuerName}
                </Typography>
                {issuerWebsite ? (
                  <Typography variant="subtitle1" sx={{ color: '#666', mb: 2 }}>
                    {issuerWebsite}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 2 }} />
                )}
                <Typography variant="body2">
                  {issuerAddressLine ? (
                    <>
                      Adresse: {issuerAddressLine}
                      <br />
                    </>
                  ) : null}
                  {issuerSiret ? (
                    <>
                      SIRET: {issuerSiret}
                      <br />
                    </>
                  ) : null}
                  {issuerPhone ? (
                    <>
                      Téléphone: {issuerPhone}
                      <br />
                    </>
                  ) : null}
                  {issuerEmail ? <>Email: {issuerEmail}</> : null}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, backgroundColor: themeTokens.infoBg, borderRadius: themeTokens.radius }}>
                <Typography variant="body2">
                  <strong>Date:</strong> {formatDate(quote.date)}<br/>
                  <strong>N° de Devis:</strong> {quote.number}<br/>
                  <strong>Réf client:</strong> {company?.name || 'Client'}<br/>
                  <strong>Statut:</strong> <span style={{ color: quote.status === 'VALIDATED' ? '#2e7d32' : quote.status === 'ACCEPTED' ? '#1976d2' : quote.status === 'REJECTED' ? '#d32f2f' : '#757575' }}>
                    {quote.status === 'DRAFT' ? 'Brouillon' : 
                     quote.status === 'VALIDATED' ? 'Validé' : 
                     quote.status === 'ACCEPTED' ? 'Accepté' : 
                     quote.status === 'REJECTED' ? 'Refusé' :
                     quote.status === 'CONVERTED' ? 'Converti' : 'En attente'}
                  </span>
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Informations client */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1F2937' }}>Client:</Typography>
              <Typography variant="body2">
                <strong>{company?.name || 'Nom du client'}</strong><br/>
                {company?.address && `${company.address}`}<br/>
                {company?.postalCode && company?.city && `${company.postalCode} ${company.city}`}<br/>
                {company?.phone && `Tél: ${company.phone}`}<br/>
                {company?.email && `Email: ${company.email}`}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Devis valide jusqu'au:</strong> {quote.validUntil ? formatDate(quote.validUntil) : 'Non spécifié'}
              </Typography>
            </Grid>
          </Grid>

          {/* Objet */}
          {quote.object && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1F2937' }}>
                Objet: {quote.object}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Tableau des prestations */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, borderRadius: themeTokens.radius, overflow: 'visible' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: themeTokens.tableHeadBg }}>
                  <TableCell sx={{ color: themeTokens.tableHeadText, fontWeight: 'bold', px: 2 }}>Prestation</TableCell>
                  <TableCell sx={{ color: themeTokens.tableHeadText, fontWeight: 'bold', px: 2 }}>Description</TableCell>
                  <TableCell align="center" sx={{ color: themeTokens.tableHeadText, fontWeight: 'bold', px: 2 }}>Prix (€)</TableCell>
                  <TableCell align="center" sx={{ color: themeTokens.tableHeadText, fontWeight: 'bold', px: 2 }}>Soumis à la TVA</TableCell>
                  <TableCell align="right" sx={{ color: themeTokens.tableHeadText, fontWeight: 'bold', px: 2 }}>Montant</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quote.items && quote.items.length > 0 ? (
                  quote.items.map((item, index) => {
                    const descriptionLines = item.description.split('\n');
                    const prestation = descriptionLines[0];
                    const details = descriptionLines.length > 1 ? descriptionLines.slice(1).join(' ') : '';
                    
                    return (
                      <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? themeTokens.stripeA : themeTokens.stripeB }}>
                        <TableCell sx={{ color: '#333', fontWeight: 'medium' }}>{prestation}</TableCell>
                        <TableCell sx={{ color: '#555' }}>
                          {details || prestation}
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#333' }}>
                          {formatCurrency(item.unitPrice * item.quantity)}
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#333' }}>
                          {item.vatRate > 0 ? 'Oui' : 'Non'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#333', fontWeight: 'medium' }}>
                          {formatCurrency(item.totalHT)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: '#666', fontStyle: 'italic', py: 4 }}>
                      Aucune prestation ajoutée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totaux */}
          <Grid container justifyContent="flex-end">
            <Grid item xs={6}>
              <Paper
                className="page-avoid-break"
                sx={{
                  p: 3,
                  backgroundColor: documentTheme === 'modern' ? rgbaFromHex('#ffffff', 0.70) : '#f9f9f9',
                  borderRadius: themeTokens.radius,
                }}
              >
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Sous-total:</Typography>
                  <Typography>{formatCurrency(quote.totalHT || 0)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>TVA ({quote.vatAmount && quote.totalHT ? `${((quote.vatAmount / quote.totalHT) * 100).toFixed(1)}%` : '0,0%'}):</Typography>
                  <Typography>{formatCurrency(quote.vatAmount || 0)}</Typography>
                </Box>
                {quote.discount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Remise exceptionnelle:</Typography>
                    <Typography>{formatCurrency(quote.discount)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6"><strong>TOTAL TTC:</strong></Typography>
                  <Typography variant="h6" sx={{ color: themeTokens.totalLabelColor, fontWeight: 'bold' }}>
                    {formatCurrency(quote.totalTTC || 0)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Pied de page */}
          <Box className="quote-footer page-avoid-break" sx={{ mt: 'auto', pt: 6 }}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Pour toute question concernant ce devis, contactez :</strong><br/>
                  {issuerName}{issuerPhone ? ` au ${issuerPhone}` : ''}{issuerEmail ? ` • ${issuerEmail}` : ''}<br/>
                  <em>Je vous remercie pour votre confiance !</em>
                </Typography>
              </Grid>
            </Grid>
            
            {quote.notes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Notes:</strong> {quote.notes}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Fermer
        </Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined">
          Imprimer
        </Button>
        <Button onClick={generatePDF} startIcon={<PdfIcon />} variant="contained">
          Télécharger PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotePreviewDialog;